<style lang="scss">
    @import '../../sass/variables';

    .data-table-search {
        background-color: $primary;
    }
</style>

<template>
    <div class="flex justify-content-end input-group data-table-search">
        <input type="text" v-model="searchString" placeholder="Type to Search" />
        <div class="input-group-append">
            <i class="fa fa-search my-auto"></i>
        </div>
    </div>

    <table :id="tableId" class="table" :data-name="tableLabel">
        <thead>
            <tr>
                <th v-for="field in fields"  :key="field.key" @click="onSortBy(field)">
                    {{ field.label }}
                    <template v-if="field.sortable">
                        <span v-show="sortField?.key != field.key"><i class="fa fa-sort" aria-hidden="true"></i></span>
                        <span v-show="sortField && sortField.key == field.key && sortDesc"><i class="fa fa-sort-down" aria-hidden="true"></i></span>
                        <span v-show="sortField && sortField.key == field.key && !sortDesc"><i class="fa fa-sort-up" aria-hidden="true"></i></span>
                    </template>
                </th>
            </tr>
        </thead>
        <tbody>
            <!-- empty table alert -->
            <tr v-if="tableItems.length == 0 || isLoading">
                <td :colspan="fields.length">
                    <div v-if="tableItems.length == 0" role="alert" aria-live="polite">
                        <div class="text-center my-2">
                            {{ emptyText }}
                        </div>
                    </div>
                    <div v-if="isLoading" class="spinner-border" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </td>
            </tr>

            <template v-else v-for="item in tableItems">
                <tr>
                    <template v-for="field in fields">
                        <td>
                            <slot :name="field.key" :item="item">
                                {{ getDisplayValue(item, field) }}
                            </slot>
                        </td>
                    </template>
                </tr>
            </template>
        </tbody>
    </table>
</template>

<script>
    import _ from 'lodash';
    import moment from 'moment'

    export default {
        props: {
            tableId: {
                type: String,
                required: false,
                default: ""
            },
            tableLabel: {
                type: String,
                required: false,
                default: ""
            },
            fields: {
                type: Array,
                required: true
            },
            items: {
                type: Array,
                required: true,
                default: []
            },
            emptyText: {
                type: String,
                required: false,
                default: "There are no items to display"
            },
            loading: {
                type: Boolean,
                required: false,
                default: false
            }
        },
        components: {
        },
        data() {
            return {
                sortField:
                {
                    key: null,
                    label: null,
                    sortable: false,
                    compareFn: undefined
                },
                sortDesc: false,
                searchString: "",
            };
        },
        methods: {
            toggleSortDir() {
                this.sortDesc = !this.sortDesc
            },
            compareString: function(a, b) {
                if(a < b)
                    return -1;
                if(a > b)
                    return 1;

                return 0;
            },
            compareDate: function(a, b) {
                let aDate = new Date(a);
                let bDate = new Date(b);
                return aDate - bDate;
            },
            /**
             * Return compare function using @param key to get value from objects being compared
             * @param key
             */
             compareByKey: function(aItem, bItem) {
                const key = this.sortField.compareKey ? this.sortField.compareKey : this.sortField.key;
                const aVal = _.get(aItem, key)
                const bVal = _.get(bItem, key)

                // check for falsey values
                if(!aVal)
                    return 1
                if(!bVal)
                    return -1

                return this.compareString(aVal, bVal);
            },
            compareDateByKey: function(aItem, bItem) {
                const key = this.sortField.compareKey ? this.sortField.compareKey : this.sortField.key;
                const aVal = _.get(aItem, key)
                const bVal = _.get(bItem, key)

                return this.compareDate(aVal,bVal);
            },
            onSortBy(field) {
                if(field.sortable) {
                    if(field.key === this.sortField.key) {
                        this.toggleSortDir();
                    } else {
                        this.sortDesc = false;
                    }
                    this.sortField = field;
                    this.resetPage();
                }
            },
            setPage: function(page) {
                this.currentPage = page;
            },
            nextPage: function() {
                if(this.currentPage >= this.pageCount) {
                    this.currentPage = this.pageCount;
                } else {
                    this.currentPage++;
                }
            },
            prevPage: function() {
                if(this.currentPage <= 1) {
                    this.currentPage = 1;
                } else {
                    this.currentPage--;
                }
            },
            resetPage: function() {
                this.setPage(1);
            },
            /**
             * determine function to use for compare based on compareFn in sortField
             */
            resolveCompareFn(fnName) {
                if(fnName && this[fnName]) {
                    return this[fnName]
                }
                return this["compareString"];
            },
            formatDate(dateString, format = 'MM/DD/YYYY') {
                if (dateString && moment(dateString).isValid()) {
                    return moment(dateString).utc().format(format)
                }
                return null
            },
            resolveFormatFn(fnName) {
                let formatFunc = () => { return 0; };

                if(fnName && this[fnName]) {
                    formatFunc = this[fnName];
                }

                return formatFunc;
            },
            fromDisplayKey(item, field) {
                if(field.displayKey) {
                    return _.get(item, field.displayKey);
                }
                return item[field.key];
            },
            formattedItem(item, field) {
                let formatFn;

                if(field.formatFn) {
                    formatFn = this.resolveFormatFn(field.formatFn);
                    return formatFn(this.fromDisplayKey(item, field), field.formatOptions.format);
                }
                return this.fromDisplayKey(item, field);
            },
            getDisplayValue(item, field) {
                let displayVal; 
                
                if(field.displayKey) {
                    displayVal = item[field.displayKey]
                } else {
                    displayVal = item[field.key];
                }

                if(field.formatFn) {
                    let formatFn = this.resolveFormatFn(field.formatFn);
                    displayVal = formatFn(this.fromDisplayKey(item, field), field.formatOptions.format);
                }

                return displayVal;
            },
        },
        computed: {
            tableItems: function() {
                if(this.searchString) {
                    return this.filteredBySearchString;
                }
                return this.sortedItems;
            },
            isLoading: function() {
                return this.loading;
            },
            sortedItems: function() {
                let sortedItems = this.filteredBySearchString;

                if(this.sortField) {
                    let compFunc = this.resolveCompareFn(this.sortField.compareFn);
                    sortedItems = sortedItems.toSorted(compFunc);
                    sortedItems = this.sortDesc ? sortedItems.toReversed() : sortedItems ;
                }

                return sortedItems;
            },
            filteredBySearchString: function() {
                if(this.searchString) {
                    return this.items.filter((item) => {
                        let searchableItem = {
                            ...item
                        };
                        return JSON.stringify(searchableItem).search(new RegExp(this.searchString, "gi")) >= 0;
                    });
                } else {
                    return this.items;
                }
            },
        },
        watch: {
            /**
             * Set remainder of sortField keys when sort dropdown is selected
             */
            "sortField.key"(newValue) {
                let newSortField = this.fields.find((field) => {
                    return field.key === newValue;
                });
                Object.assign(this.sortField,  { ...newSortField });
            },
        },
        mounted() {
        }
    }
</script>

